import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ScanSearch, Circle, XCircle, Shield, FolderTree, Hash, Volume2, Users, Lock, Archive, Zap, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useData } from "@/context/DataContext";
import { initialSyncApi, apiError } from "@/lib/api";

const STEPS = [
  "Collegamento al bot",
  "Lettura dei ruoli e dei loro permessi",
  "Lettura di categorie e canali",
  "Lettura dei permessi di ogni canale",
  "Lettura dei membri",
  "Lettura delle funzioni attive del bot",
  "Salvataggio di un backup dello stato iniziale",
];

function Tile({ icon: Icon, value, label, tone = "#5865F2" }) {
  return (
    <div className="rounded-xl border border-[#232838] bg-[#131620] p-3">
      <Icon className="mb-1.5 h-4 w-4" style={{ color: tone }} />
      <p className="text-xl font-extrabold text-white">{value ?? 0}</p>
      <p className="text-xs text-[#949BA4]">{label}</p>
    </div>
  );
}

export function InitialSyncDialog({ open, onOpenChange, onDone }) {
  const { refresh, refreshStatus } = useData();
  const [phase, setPhase] = useState("intro"); // intro | running | done | error
  const [step, setStep] = useState(0);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => { if (open) { setPhase("intro"); setStep(0); setReport(null); setError(null); } }, [open]);
  useEffect(() => () => clearInterval(timer.current), []);

  const run = async () => {
    setPhase("running"); setStep(0);
    // Avanzamento visivo mentre il server lavora; l'ultimo passo si chiude solo a risposta arrivata.
    timer.current = setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 650);
    try {
      const r = await initialSyncApi.run();
      clearInterval(timer.current);
      setStep(STEPS.length);
      setReport(r.report);
      setPhase("done");
      await refresh(); refreshStatus();
      onDone?.(r);
      toast.success("Prima sincronizzazione completata");
    } catch (e) {
      clearInterval(timer.current);
      setError(apiError(e)); setPhase("error");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => phase !== "running" && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232838] bg-[#161923] text-[#DBDEE1] sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><ScanSearch className="h-5 w-5 text-[#00F0FF]" />Prima sincronizzazione</DialogTitle>
          <DialogDescription className="text-[#949BA4]">Il bot legge tutto il tuo server Discord e lo porta nel sito, così parti da una base già pronta.</DialogDescription>
        </DialogHeader>

        {phase === "intro" && (
          <div className="space-y-4">
            <ul className="space-y-2 text-sm">
              {["Tutti i ruoli, con colori, ordine e permessi", "Tutte le categorie e i canali, nell'ordine di Discord", "I permessi personalizzati di ogni canale (chi vede, chi scrive…)", "Tutti i membri con i loro ruoli", "Un backup automatico dello stato di partenza, per tornare indietro quando vuoi"].map((t) => (
                <li key={t} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#23A55A]" />{t}</li>
              ))}
            </ul>
            <p className="rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/[0.06] p-3 text-sm text-[#C9CDD4]">Non modifica nulla su Discord: legge soltanto. Puoi rifarla ogni volta che vuoi.</p>
            <Button onClick={run} className="w-full bg-gradient-to-r from-[#5865F2] to-[#8B5CF6] py-5 font-bold hover:opacity-90" data-testid="initial-sync-run"><ScanSearch className="mr-2 h-5 w-5" />Avvia la scansione</Button>
          </div>
        )}

        {(phase === "running" || phase === "error") && (
          <div className="space-y-2">
            {STEPS.map((s, i) => {
              const state = phase === "error" && i === step ? "error" : i < step ? "done" : i === step ? "active" : "todo";
              return (
                <div key={s} className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${state === "active" ? "bg-[#5865F2]/10 text-white" : state === "done" ? "text-[#C9CDD4]" : "text-[#6b7185]"}`}>
                  {state === "done" && <CheckCircle2 className="h-4 w-4 text-[#23A55A]" />}
                  {state === "active" && <Loader2 className="h-4 w-4 animate-spin text-[#8ea0ff]" />}
                  {state === "todo" && <Circle className="h-4 w-4" />}
                  {state === "error" && <XCircle className="h-4 w-4 text-[#F23F43]" />}
                  {s}
                </div>
              );
            })}
            {phase === "error" && (
              <div className="space-y-3 pt-2">
                <p className="rounded-xl border border-[#F23F43]/30 bg-[#F23F43]/10 p-3 text-sm text-[#ff9a9c]">{error}</p>
                <Button onClick={run} className="w-full bg-[#5865F2] hover:bg-[#4752C4]">Riprova</Button>
              </div>
            )}
          </div>
        )}

        {phase === "done" && report && (
          <div className="space-y-4" data-testid="initial-sync-report">
            <div className="flex items-center gap-3 rounded-xl border border-[#23A55A]/30 bg-[#23A55A]/10 p-3">
              <CheckCircle2 className="h-6 w-6 shrink-0 text-[#23A55A]" />
              <div><p className="font-bold text-white">«{report.guild}» è stato importato</p><p className="text-sm text-[#C9CDD4]">Ora puoi lavorare dal sito partendo esattamente da com'è il server.</p></div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Tile icon={Shield} value={report.roles} label="ruoli" tone="#8ea0ff" />
              <Tile icon={FolderTree} value={report.categories} label="categorie" tone="#00F0FF" />
              <Tile icon={Hash} value={report.text + report.announcement + report.forum} label="canali testuali" tone="#23A55A" />
              <Tile icon={Volume2} value={report.voice} label="canali vocali" tone="#F47FFF" />
              <Tile icon={Lock} value={report.permissionOverwrites} label="permessi di canale" tone="#FEE75C" />
              <Tile icon={Lock} value={report.privateChannels} label="canali privati" tone="#F97316" />
              <Tile icon={Users} value={report.humans} label="membri" tone="#8ea0ff" />
              <Tile icon={Bot} value={report.bots} label="bot" tone="#949BA4" />
            </div>
            {report.activeFeatures?.length > 0 && (
              <p className="flex flex-wrap items-center gap-1.5 text-sm text-[#C9CDD4]"><Zap className="h-4 w-4 text-[#FEE75C]" />Funzioni del bot già attive:
                {report.activeFeatures.map((f) => <span key={f} className="rounded-full bg-[#232838] px-2 py-0.5 text-xs text-white">{f}</span>)}
              </p>
            )}
            {report.backupId && <p className="flex items-center gap-2 text-sm text-[#C9CDD4]"><Archive className="h-4 w-4 text-[#8ea0ff]" />Backup «Stato iniziale» salvato nella pagina Backup.</p>}
            <div className="grid gap-2 sm:grid-cols-2">
              <Button asChild className="bg-[#5865F2] hover:bg-[#4752C4]"><Link to="/canali" onClick={() => onOpenChange(false)}>Apri Canali</Link></Button>
              <Button asChild variant="outline" className="border-[#232838] bg-transparent text-[#B5BAC1] hover:bg-[#252B3D] hover:text-white"><Link to="/ruoli" onClick={() => onOpenChange(false)}>Apri Ruoli</Link></Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Riquadro in Panoramica: invita a fare la prima sincronizzazione (o mostra quando è stata fatta).
export function InitialSyncBanner() {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => initialSyncApi.info().then(setInfo).catch(() => setInfo({ done: false }));
  useEffect(() => { load(); }, []);
  if (!info) return null;
  // Una sola finestra per entrambe le versioni del riquadro, così il resoconto resta visibile a fine scansione.
  const dialog = <InitialSyncDialog open={open} onOpenChange={setOpen} onDone={load} />;
  const content = info.done ? (
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#232838] bg-[#161923] px-4 py-3 text-sm">
        <CheckCircle2 className="h-4 w-4 text-[#23A55A]" />
        <span className="text-[#C9CDD4]">Prima sincronizzazione fatta {new Date(info.at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })} da {info.by}.</span>
        <button onClick={() => setOpen(true)} className="ml-auto font-semibold text-[#8ea0ff] hover:underline" data-testid="initial-sync-open">Riscansiona</button>
      </div>
  ) : (
    <div className="relative overflow-hidden rounded-2xl border border-[#00F0FF]/25 bg-gradient-to-r from-[#5865F2]/20 via-[#8B5CF6]/10 to-[#00F0FF]/10 p-5" data-testid="initial-sync-banner">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00F0FF] to-[#5865F2] shadow-[0_0_30px_-6px_#00F0FF]"><ScanSearch className="h-6 w-6 text-white" /></div>
        <div className="flex-1">
          <p className="text-lg font-extrabold text-white">Fai la prima sincronizzazione</p>
          <p className="text-sm text-[#C9CDD4]">Il bot scansiona ruoli, canali, permessi e membri del tuo server e li porta qui: parti da una base già pronta.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-white font-bold text-[#1e1f22] hover:bg-white/90" data-testid="initial-sync-open">Inizia</Button>
      </div>
    </div>
  );
  return <>{content}{dialog}</>;
}
